/**
 * touch-controls.js -- an on-screen touch overlay for the 12 SNES buttons,
 * fast forward, and save/load-state shortcuts, for playing on a touchscreen
 * with no keyboard and a gamepad that (as it turns out for plenty of cheap
 * Bluetooth pads) doesn't reliably expose every physical button to the
 * browser.
 *
 * Off by default, lazily created the first time it's enabled (same pattern
 * as gl-filters.js's GLFilterRenderer). SNES-button and action controls feed
 * InputManager via setTouchButton()/setTouchAction() -- the exact same
 * per-port merge path keyboard and gamepad input already go through, so
 * touch is just a third input source, not a special case anywhere else in
 * the app. Save/load/slot controls are one-shot taps instead (there's
 * nothing to "hold"), so they call back into an onTrigger(name) the caller
 * supplies rather than going through InputManager at all.
 *
 * Buttons are draggable while edit mode is on (pointer capture, so a drag
 * that wanders off the button's visual bounds still tracks correctly) and
 * persist position as {x,y} PERCENTAGES of the stage's own box -- not
 * pixels -- so the layout holds up across window resizes and orientation
 * changes instead of drifting off-screen. Each button can also be removed
 * from the layout individually (a small "x" shown only in edit mode); a
 * removed button stays visible-but-faded while editing, so it can be found
 * and restored, and disappears entirely otherwise. "Reset layout" restores
 * both position and visibility for everything.
 */

import { JOYPAD } from './input.js';

const LAYOUT_STORAGE_KEY = 'snes9x-web:touch-layout';

/**
 * Default layout, as percentages of the stage box. Roughly: d-pad cross
 * bottom-left, SNES-diamond face buttons bottom-right (Y left, X top, B
 * bottom, A right -- matching the physical pad), L/R at the top corners,
 * Start/Select bottom-centre, fast forward top-centre, save/load/slot
 * shortcuts in a row below that.
 */
const CONTROLS = [
  { id: 'UP', label: '▲', joypad: JOYPAD.UP, x: 15, y: 63 },
  { id: 'DOWN', label: '▼', joypad: JOYPAD.DOWN, x: 15, y: 90 },
  { id: 'LEFT', label: '◀', joypad: JOYPAD.LEFT, x: 4, y: 76.5 },
  { id: 'RIGHT', label: '▶', joypad: JOYPAD.RIGHT, x: 26, y: 76.5 },
  { id: 'Y', label: 'Y', joypad: JOYPAD.Y, x: 74, y: 76.5 },
  { id: 'X', label: 'X', joypad: JOYPAD.X, x: 85, y: 63 },
  { id: 'B', label: 'B', joypad: JOYPAD.B, x: 85, y: 90 },
  { id: 'A', label: 'A', joypad: JOYPAD.A, x: 96, y: 76.5 },
  { id: 'L', label: 'L', joypad: JOYPAD.L, x: 6, y: 10, wide: true },
  { id: 'R', label: 'R', joypad: JOYPAD.R, x: 94, y: 10, wide: true },
  { id: 'SELECT', label: 'Select', joypad: JOYPAD.SELECT, x: 40, y: 96, wide: true },
  { id: 'START', label: 'Start', joypad: JOYPAD.START, x: 60, y: 96, wide: true },
  { id: 'FF', label: 'FF', action: 'fastForward', x: 50, y: 10, wide: true },
  { id: 'SAVE', label: 'Save', trigger: 'saveState', x: 25, y: 20, wide: true },
  { id: 'LOAD', label: 'Load', trigger: 'loadState', x: 75, y: 20, wide: true },
  { id: 'SLOT', label: 'Slot+', trigger: 'nextSlot', x: 50, y: 22, wide: true },
  { id: 'SLOTDOWN', label: 'Slot−', trigger: 'prevSlot', x: 50, y: 34, wide: true },
];

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function loadLayout() {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLayout(layout) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* storage unavailable or full: layout just won't survive a reload */
  }
}

export class TouchControls {
  /**
   * @param {HTMLElement} stage the element controls are positioned relative to
   * @param {import('./input.js').InputManager} input
   * @param {{ onTrigger?: (name: string) => void }} [options] called once per
   *   tap for a one-shot control (saveState/loadState/nextSlot), never for a
   *   held SNES button/action
   */
  constructor(stage, input, { onTrigger } = {}) {
    this.stage = stage;
    this.input = input;
    this.onTrigger = onTrigger;
    this.editMode = false;
    this.layout = loadLayout();

    this.root = document.createElement('div');
    this.root.className = 'touch-controls';
    this.root.hidden = true;

    this._buttons = new Map(); // id -> HTMLElement
    this._labels = new Map(); // id -> label <span>, for setLabel()
    for (const spec of CONTROLS) {
      const el = this._buildButton(spec);
      this._buttons.set(spec.id, el);
      this.root.append(el);
    }

    this.stage.append(this.root);
  }

  setEnabled(enabled) {
    this.root.hidden = !enabled;
    if (!enabled) this.input.clearTouchInput();
  }

  setEditMode(editMode) {
    this.editMode = editMode;
    this.root.classList.toggle('editing', editMode);
    if (editMode) this.input.clearTouchInput(); // don't leave a button "held" from just before entering edit mode
  }

  /** Override a button's displayed text -- used for e.g. showing the current slot number on the Slot+ button. */
  setLabel(id, text) {
    const label = this._labels.get(id);
    if (label) label.textContent = text;
  }

  resetLayout() {
    this.layout = {};
    saveLayout(this.layout);
    for (const spec of CONTROLS) this._applyLayout(this._buttons.get(spec.id), spec);
  }

  _entryFor(spec) {
    return { x: spec.x, y: spec.y, removed: false, ...this.layout[spec.id] };
  }

  _applyLayout(el, spec) {
    const { x, y, removed } = this._entryFor(spec);
    el.style.left = `${x}%`;
    el.style.top = `${y}%`;
    el.classList.toggle('removed', removed);
  }

  _setRemoved(spec, removed) {
    this.layout[spec.id] = { ...this._entryFor(spec), removed };
    saveLayout(this.layout);
  }

  _buildButton(spec) {
    const el = document.createElement('div');
    el.className = `touch-btn${spec.wide ? ' touch-btn-wide' : ''}`;
    el.dataset.control = spec.id;

    const labelEl = document.createElement('span');
    labelEl.className = 'touch-btn-label';
    labelEl.textContent = spec.label;
    this._labels.set(spec.id, labelEl);
    el.append(labelEl);

    // Only interactive/visible while editing (CSS gates it) -- toggles
    // whether this button shows up outside edit mode at all. A removed
    // button stays visible-but-faded during editing so it can be restored.
    const removeBtn = document.createElement('span');
    removeBtn.className = 'touch-btn-remove';
    removeBtn.textContent = '×';
    removeBtn.addEventListener('pointerdown', (event) => event.stopPropagation());
    removeBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      const nowRemoved = !el.classList.contains('removed');
      this._setRemoved(spec, nowRemoved);
      el.classList.toggle('removed', nowRemoved);
    });
    el.append(removeBtn);

    this._applyLayout(el, spec);

    let pressed = false;
    let dragging = false;
    let dragPointerId = null;

    const setPressed = (v) => {
      if (pressed === v) return;
      pressed = v;
      el.classList.toggle('pressed', v);
      if (spec.trigger !== undefined) {
        if (v) this.onTrigger?.(spec.trigger); // one-shot: fires on press, not on release
      } else if (spec.joypad !== undefined) {
        this.input.setTouchButton(spec.joypad, v);
      } else {
        this.input.setTouchAction(spec.action, v);
      }
    };

    el.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        // Capture keeps a drag/press "owned" by this element even if the
        // finger slides off its bounds -- a nice-to-have, not required for
        // press/release to work at all, so a failure here (synthetic event
        // sources, or any other edge case) shouldn't abort the handler.
      }
      if (this.editMode) {
        dragging = true;
        dragPointerId = event.pointerId;
        el.classList.add('dragging');
      } else {
        setPressed(true);
      }
    });

    el.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== dragPointerId) return;
      const rect = this.stage.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 2, 98);
      const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 2, 98);
      this.layout[spec.id] = { ...this._entryFor(spec), x, y };
      el.style.left = `${x}%`;
      el.style.top = `${y}%`;
    });

    const endPointer = (event) => {
      if (dragging && event.pointerId === dragPointerId) {
        dragging = false;
        dragPointerId = null;
        el.classList.remove('dragging');
        saveLayout(this.layout);
      }
      setPressed(false);
    };
    el.addEventListener('pointerup', endPointer);
    el.addEventListener('pointercancel', endPointer);

    return el;
  }
}
